import React, { useState } from "react";
import { Calendar, Users, UserRound, Settings, CreditCard, BarChart3, Menu, X, DollarSign, type LucideIcon } from "./icons";
import { useLanguage } from "../contexts/LanguageContext";
import { sidebarLabels } from "./Sidebar.labels";

export type MenuItem = {
  href: string;
  label: string;
  Icon: LucideIcon;
  match: (path: string) => boolean;
};

export const menuItems: MenuItem[] = [
  {
    href: "/app/appointments",
    label: "Agenda",
    Icon: Calendar,
    match: (path) => path.startsWith("/app/appointments"),
  },
  {
    href: "/app/clients",
    label: "Clientes",
    Icon: Users,
    match: (path) => path.startsWith("/app/clients"),
  },
  {
    href: "/app/employees",
    label: "Profissionais",
    Icon: UserRound,
    match: (path) => path.startsWith("/app/employees"),
  },
  {
    href: "/app/metrics",
    label: "Métricas",
    Icon: BarChart3,
    match: (path) => path.startsWith("/app/metrics"),
  },
  {
    href: "/app/financial",
    label: "Financeiro",
    Icon: DollarSign,
    match: (path) => path.startsWith("/app/financial"),
  },
  {
    href: "/app/settings",
    label: "Configurações",
    Icon: Settings,
    match: (path) => path.startsWith("/app/settings"),
  },
  {
    href: "/app/assinatura",
    label: "Assinatura",
    Icon: CreditCard,
    match: (path) => path.startsWith("/app/assinatura"),
  },
];

export type SidebarProps = {
  expanded: boolean;
  path: string;
  onNavigate: (href: string) => void;
  onToggle: () => void;
  userName?: string | null;
  onLogout: () => void;
  className?: string;
  title?: string;
  items?: MenuItem[];
};

export function Sidebar({ expanded, path, onNavigate, onToggle, userName, onLogout, className, title, items }: SidebarProps) {
  const { lang } = useLanguage();
  const t = sidebarLabels[lang];
  const [hovering, setHovering] = useState(false);
  const effectiveExpanded = expanded || hovering;
  const menu = items ?? menuItems;
  return (
    <aside
      className={
        `hidden md:flex border-neutral-200 bg-white overflow-hidden flex-col transition-[width] duration-300 ease-in-out border-r sticky top-0 h-screen ` +
        (effectiveExpanded ? "w-60" : "w-16") + (className ? ` ${className}` : "")
      }
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div className="h-14 px-3 flex items-center justify-between font-semibold">
        <span className={`transition-opacity duration-200 ${effectiveExpanded ? "opacity-100" : "opacity-0"}`}>{title || "Agenda"}</span>
      </div>
      <nav className="px-2 py-2 space-y-1">
        {menu.map(({ href, label, Icon, match }) => {
          const active = match(path);
          return (
            <a
              key={href}
              href={href}
              onClick={(e) => { e.preventDefault(); onNavigate(href); }}
              className={`flex items-center gap-3 rounded-md text-sm px-3 py-2 transition-colors ${
                active ? "bg-neutral-900 text-white" : "text-neutral-800 hover:bg-neutral-100"
              }`}
              title={!effectiveExpanded ? label : undefined}
            >
              <Icon className="w-5 h-5" />
              <span className={`${effectiveExpanded ? "inline" : "hidden"}`}>{label}</span>
            </a>
          );
        })}
      </nav>
      <div className={`mt-auto p-3 text-xs text-neutral-500 ${effectiveExpanded ? "block" : "hidden"}`}>
        <div>{userName}</div>
        <button
          className="mt-2 text-left underline underline-offset-2"
          onClick={onLogout}
        >{t.logout}</button>
      </div>
    </aside>
  );
}
